import { FetchResult } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProductTypePropertiesInputType } from '@deltek/specification-client/core/Models/productType'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { CreateProjectManufacturerProductListingInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import {
    SearchFacetInput,
    SearchFacets,
    SelectedManufacturerListing,
} from 'api/graphQL/types/product.type'
import { UpdatedListingSearchInput } from 'api/graphQL/types/project.types'
import { WorkspaceManufacturerSelected } from 'shared/utils/workspaceUtils'
import {
    DisplayTextFormatType,
    ListingDisplayTextFormat,
    PtpOptionsToInclude,
} from './ProductListingDialog.types'

export const toggleSpecialtyFacet = (
    past: Partial<UpdatedListingSearchInput>,
    facetName: string,
    enabled: boolean
): Partial<UpdatedListingSearchInput> => ({
    ...past,
    facets: enabled
        ? [
              ...(past?.facets ?? []).filter((f) => f.name !== facetName),
              { name: facetName, values: ['true'] },
          ]
        : (past?.facets ?? []).filter((f) => f.name !== facetName),
})

export const defaultProductTypeSelections = (
    hasFamily: boolean,
    family: string,
    hasProductType: boolean,
    productType: string,
    ptpOptions?: ProductTypePropertiesInputType[]
) => {
    if (hasFamily && hasProductType) {
        return [
            {
                sectionName: family,
                productTypeName: productType,
                productTypeProperties: ptpOptions?.map((ptpOption) => ({
                    propertyName: ptpOption.propertyName.toString(),
                    propertyOptionIds: ptpOption.propertyOptionIds.map(
                        (property) => property.toString().normalize('NFKC')
                    ),
                })),
            },
        ]
    }
    return []
}

interface ResolveAddListingParams {
    selectedPT: any
    isProductType: boolean
    isProductFamily: boolean
    nuggetProductFamilyId: string | undefined
    listingMatchElementId: string | null | undefined
    elementId: string | null | undefined
    listingMatchProductType: string
    displayTextFormat: DisplayTextFormatType
    withMasterSpecOption: boolean
    withManufacturerOption: boolean
}

interface ResolvedAddListingValues {
    hasSelectedPT: boolean
    isProductTypeWithNugget: boolean
    isProductFamilyOrType: boolean
    resolvedProductFamilyId: string
    resolvedProjectNuggetId: string
    resolvedProjectElementType: ProjectElementType
    resolvedProductType: string
    resolvedDisplayTextFormat: ListingDisplayTextFormat
    resolvedPtpOptionsToInclude: PtpOptionsToInclude
}

/**
 * Resolves complex conditional logic for adding a listing to a project.
 * Extracts nested ternary operations into clear, descriptive variables.
 */
export const resolveAddListingToProjectParams = (
    params: ResolveAddListingParams
): ResolvedAddListingValues => {
    const {
        selectedPT,
        isProductType,
        isProductFamily,
        nuggetProductFamilyId,
        listingMatchElementId,
        elementId,
        listingMatchProductType,
        displayTextFormat,
        withMasterSpecOption,
        withManufacturerOption,
    } = params

    // Common condition variables
    const hasSelectedPT = !!selectedPT
    const isProductTypeWithNugget = isProductType && !!nuggetProductFamilyId
    const isProductFamilyOrType = isProductFamily || isProductType

    // Determine product family ID based on context
    const resolvedProductFamilyId = hasSelectedPT
        ? selectedPT?.familyElementId ?? ''
        : isProductTypeWithNugget
        ? nuggetProductFamilyId ?? ''
        : isProductFamilyOrType
        ? listingMatchElementId ?? ''
        : ''

    // Determine project nugget ID
    const resolvedProjectNuggetId = hasSelectedPT
        ? selectedPT?.pTElementId ?? ''
        : isProductType
        ? elementId ?? ''
        : ''

    // Determine project element type
    const resolvedProjectElementType = hasSelectedPT
        ? ProjectElementType.PRODUCTTYPE
        : isProductTypeWithNugget
        ? ProjectElementType.PRODUCTTYPE
        : isProductFamilyOrType
        ? ProjectElementType.PRODUCTFAMILY
        : ProjectElementType.ASSEMBLY

    // Determine product type
    const resolvedProductType = hasSelectedPT
        ? selectedPT?.value
        : listingMatchProductType ?? ''

    // Determine display text format
    const resolvedDisplayTextFormat =
        displayTextFormat === 'Manufacturer'
            ? ListingDisplayTextFormat.Manufacturer
            : displayTextFormat === 'None'
            ? ListingDisplayTextFormat.None
            : ListingDisplayTextFormat.ManufacturerAndProduct

    // Determine PTP options to include
    const resolvedPtpOptionsToInclude =
        withMasterSpecOption && withManufacturerOption
            ? PtpOptionsToInclude.MasterSpecAndManufacturerOptions
            : withMasterSpecOption
            ? PtpOptionsToInclude.MasterSpecOptions
            : PtpOptionsToInclude.NoOptions

    return {
        hasSelectedPT,
        isProductTypeWithNugget,
        isProductFamilyOrType,
        resolvedProductFamilyId,
        resolvedProjectNuggetId,
        resolvedProjectElementType,
        resolvedProductType,
        resolvedDisplayTextFormat,
        resolvedPtpOptionsToInclude,
    }
}

export const checkPPLErrors = (
    value: FetchResult<any, Record<string, any>, Record<string, any>>
) => {
    // Check for GraphQL errors (can occur even with HTTP 200)
    // Apollo can return errors as either an array or a single Error object
    if (value.errors) {
        let errorMessage = 'An error occurred'

        // Handle errors as array
        if (Array.isArray(value.errors) && value.errors.length > 0) {
            errorMessage = value.errors[0].message
        }
        // Handle errors as single Error object
        else if (value.errors instanceof Error) {
            errorMessage = value.errors.message
        }
        // Handle errors as object with message property
        else if (
            typeof value.errors === 'object' &&
            'message' in value.errors
        ) {
            errorMessage = (value.errors as any).message
        }

        return Promise.reject(new Error(errorMessage))
    }

    // Check if data is undefined (usually indicates an error)
    if (!value.data) {
        return Promise.reject(new Error('Mutation failed - no data returned'))
    }
}

/**
 * Listing levels a Contributor is allowed to add to a project.
 *
 * `Product Listing` is the badgeless-cards counterpart of `Manufacturer` — the
 * backend treats them as the same tier (see ListingLevels in the manufacturer
 * products service) and the AE default filter switches to `Product Listing`
 * when REACT_APP_EnableBadgelessCards is on. Gating on `Manufacturer` alone
 * left every card in the badgeless default view disabled.
 */
export const isContributorAddableListingLevel = (
    listingLevel: string | null | undefined
): boolean =>
    listingLevel === ListingLevel.Manufacturer ||
    listingLevel === ListingLevel.ProductListing

/**
 * Normalises a product type name for cross-source comparison.
 *
 * Project tree node text (Cosmos) and a card's product type (search index) are
 * written by different systems, so the same product type can differ by unicode
 * form or whitespace — most visibly on names carrying a trailing qualifier such
 * as "Laminated Glass, Class 25". Without this the names compare unequal and
 * the product type reads as absent from the project.
 */
const normalizeProductTypeName = (value: string | null | undefined): string =>
    value
        ? value.normalize('NFKC').trim().replace(/\s+/g, ' ').toUpperCase()
        : ''

export const isProductTypeInProject = (
    productType: string | null | undefined,
    {
        isInProject,
        selectedProductType,
        productTypesInProject,
    }: {
        isInProject: boolean
        selectedProductType?: string | null
        productTypesInProject?: (string | null | undefined)[]
    }
): boolean => {
    const normalizedProductType = normalizeProductTypeName(productType)
    if (!normalizedProductType) {
        return false
    }
    return (
        (isInProject &&
            normalizeProductTypeName(selectedProductType) ===
                normalizedProductType) ||
        (productTypesInProject?.some(
            (pt) => normalizeProductTypeName(pt) === normalizedProductType
        ) ??
            false)
    )
}

/**
 * Pure decision logic for the dialog's filter-reset gate (see usage in index.tsx).
 *
 * Returns true iff the user-set facet filters should be reset to defaults. The gate
 * keeps filters in two cases:
 *
 *   1. nodeId is unchanged — typical post-mutation re-render where only elementId
 *      flipped (e.g. listing selection echo); filters should obviously persist.
 *   2. nodeId changed but the node identity is stable — the catalog->project PT
 *      promotion case, where the node's id flips from catalog UUID to project UUID
 *      while baseElementId + treePath remain the same logical element.
 *
 * baseElementId alone is NOT a unique node key: the same catalog PT can appear under
 * multiple assemblies (same baseElementId, different ancestor chain). Pair it with
 * treePath so the gate doesn't preserve filters when the user navigates between two
 * tree instances of the same catalog product type.
 *
 * On initial mount (no previousNodeId) the gate forces a reset so the dialog opens
 * with a clean facet state.
 */
export const shouldResetDialogFilters = (input: {
    nodeId: string | null | undefined
    previousNodeId: string | null | undefined
    baseElementId: string | null | undefined
    previousBaseElementId: string | null | undefined
    treePath: string | null | undefined
    previousTreePath: string | null | undefined
}): boolean => {
    const {
        nodeId,
        previousNodeId,
        baseElementId,
        previousBaseElementId,
        treePath,
        previousTreePath,
    } = input
    if (!previousNodeId) return true
    if (previousNodeId === nodeId) return false
    const isSameLogicalNode =
        !!baseElementId &&
        !!previousBaseElementId &&
        previousBaseElementId === baseElementId &&
        !!treePath &&
        !!previousTreePath &&
        previousTreePath === treePath
    return !isSameLogicalNode
}

/**
 * Resolves the `divisionNumber` sent to the autosuggest query.
 * Family/PT elements scope by nodeIds already; sending divisionNumber alongside
 * them causes a server-rejected inconsistent payload (#2828371).
 * All-products toggle always clears it regardless of element type.
 */
export const resolveAutosuggestDivisionNumber = (input: {
    isAEAllProductsToggled: boolean
    isProductFamily: boolean
    isProductType: boolean
    divisionNumber: string | null | undefined
}): string => {
    const { isAEAllProductsToggled, isProductFamily, isProductType, divisionNumber } = input
    if (isAEAllProductsToggled || isProductFamily || isProductType) return ''
    return divisionNumber ?? ''
}

type NodeIdScope = Array<string | null | undefined> | null

/**
 * Resolves the `nodeIds` scope for the ProductType / SectionNames autosuggest query.
 *
 * Previously introduced in PR 194521 and reverted (PR 194788) for release-timing reasons only —
 * not due to a bug. Re-introduced here for the current release.
 *
 * - ASSEMBLY selected: scope to the assembly's own node so the backend returns all PTs
 *   under it when no family is selected. Scoping to project-added families instead (the
 *   old approach) collapsed the PT list to a single family once anything was added
 *   (Bug 2429070 / Bug 2612243). When the user HAS selected a family filter, scope to
 *   that family's node so the PT dropdown shows only that family's PTs.
 * - PRODUCTTYPE selected: scope to its resolved parent assembly node.
 * - Otherwise: use the selected family node ids.
 */
export const resolveAutoSuggestNodeIds = (input: {
    elementType: string | null | undefined
    nodeId: string | null | undefined
    resolvedAssemblyNodeId: string | null | undefined
    selectedFamilyNodeIds: NodeIdScope
}): NodeIdScope => {
    const {
        elementType,
        nodeId,
        resolvedAssemblyNodeId,
        selectedFamilyNodeIds,
    } = input
    if (elementType === 'ASSEMBLY') {
        // If the user has selected specific families, scope PT suggestions to those
        // families. Otherwise scope to the whole assembly so all PTs are shown
        // (project-added-families scoping regressed Bug 2429070).
        const hasSelectedFamilies =
            Array.isArray(selectedFamilyNodeIds) &&
            selectedFamilyNodeIds.some((id) => !!id)
        if (hasSelectedFamilies) {
            return selectedFamilyNodeIds
        }
        return nodeId ? [nodeId] : null
    }
    return resolvedAssemblyNodeId
        ? [resolvedAssemblyNodeId]
        : selectedFamilyNodeIds
}

/**
 * Resolves the family node ids currently selected in the Families filter,
 * from the cached `productTypeFacets` (which carries the `familyNodeId` the
 * ProductFamily autosuggest reported for each family) matched against the
 * `SectionNames` entry of `searchFacetInputs`.
 *
 * For a PRODUCTFAMILY element, the workspace node's own family is a valid
 * selection (`isProductFamily ? true : ...`) — otherwise it is excluded so a
 * family filter that merely echoes the workspace node doesn't count as an
 * explicit selection.
 */
export const resolveFilterSelectedFamilyNodeIds = (input: {
    searchFacetInputs: SearchFacetInput[]
    productTypeFacets: Partial<SearchFacets>[]
    isProductFamily: boolean
    nodeId: string | null | undefined
}): string[] => {
    const { searchFacetInputs, productTypeFacets, isProductFamily, nodeId } = input

    const familyNames = searchFacetInputs.find(
        (item) => item.name === 'SectionNames'
    )

    const selectedFamilies = productTypeFacets
        .filter((fam) =>
            familyNames?.values.some(
                (each) =>
                    !!fam.familyNodeId &&
                    (isProductFamily ? true : nodeId !== fam.familyNodeId) &&
                    each.trim() === fam.parentProductFamily?.trim()
            )
        )
        ?.reduce((prev: Partial<SearchFacets>[], current) => {
            const isExist = prev.find(
                (item) =>
                    item.parentProductFamily === current.parentProductFamily
            )
            if (!isExist) {
                return prev.concat([current])
            } else {
                return prev
            }
        }, [])

    return Array.from(
        new Set(
            selectedFamilies
                .map((item) => item.familyNodeId)
                .filter((id): id is string => !!id)
        )
    )
}

/**
 * Resolves the `resolvedAssemblyNodeId` passed into `resolveAutoSuggestNodeIds`
 * for the ProductType autosuggest query.
 *
 * The assembly/parent-family fallback must yield to an explicit Families filter
 * selection — otherwise `resolveAutoSuggestNodeIds` prefers the fallback over the
 * filter (see its PRODUCTTYPE branch) and the ProductType dropdown keeps querying
 * the workspace node's own family even after the user filters by a different one
 * (Bug 2735643).
 */
export const resolveAssemblyNodeIdForAutoSuggest = (input: {
    isProductType: boolean
    hasFilterSelectedFamilies: boolean
    assemblyNodeId: string | null | undefined
}): string | null => {
    const { isProductType, hasFilterSelectedFamilies, assemblyNodeId } = input
    if (!isProductType || hasFilterSelectedFamilies) {
        return null
    }
    return assemblyNodeId ?? null
}

/**
 * Resolves which parent node ids `fetchProjectPTs` should seed its PT lookup
 * from, for the dialog's current entry point element type.
 *
 * A PRODUCTFAMILY or last-sub-assembly node seeds from its own nodeId. A
 * PRODUCTTYPE node has no PT children of its own — its siblings live under
 * its parent family node instead, so it must seed from `familyNodeIdForPT`.
 * Returns null when the entry point isn't one of these (fetch should be
 * skipped entirely).
 */
export const resolveProjectPTsFetchParentIds = (input: {
    isProductFamily: boolean
    isLastSubAssembly: boolean
    isProductType: boolean
    nodeId: string | null | undefined
    familyNodeIdForPT: string | null | undefined
}): string[] | null => {
    const {
        isProductFamily,
        isLastSubAssembly,
        isProductType,
        nodeId,
        familyNodeIdForPT,
    } = input

    const canFetch =
        isProductFamily ||
        isLastSubAssembly ||
        (isProductType && !!familyNodeIdForPT)

    if (!canFetch) {
        return null
    }

    if (isProductType && familyNodeIdForPT) {
        return [familyNodeIdForPT]
    }

    return nodeId ? [nodeId] : null
}

/**
 * Decides whether `getProductFacetOptions` should narrow its result to the
 * families currently selected in the Families filter.
 *
 * Only the ProductType dropdown narrows this way. The Families dropdown must
 * keep every suggested family as an option — `DialogFilter` uses this list as
 * a metadata lookup to attach `familyNodeId`/`familyElementId` to a newly
 * selected family, and narrowing by the *previously* selected families would
 * leave a newly picked family without those ids (Bug 2735643).
 */
export const shouldFilterProductFacetOptionsByFamily = (
    facet: string,
    selectedFamilies: string[]
): boolean => facet === 'ProductType' && selectedFamilies.length > 0

export interface BulkAddWorkspaceMessages {
    elementId: string
    nodeId?: string | null
    manufacturerSelections: WorkspaceManufacturerSelected[]
    /** null when the mutation returned no contentItems array. */
    workspaceItems: {
        contentItems: ContentItem[]
        input: Partial<CreateProjectManufacturerProductListingInput>
    } | null
}

/**
 * Builds the service worker payloads for a completed bulk manufacturer add.
 *
 * `elementId`/`nodeId` resolve to the newly promoted product type when the mutation
 * created one — matched by elementId, since projectElementNodes is ordered
 * ancestors-first — and otherwise to the dialog's own element and node.
 * `workspaceItems` carries the returned content items that the classic workspace
 * refreshes its manufacturer pills from; its listingId is only a presence gate
 * downstream, so the first listing represents the batch.
 */
export const resolveBulkAddWorkspaceMessages = (input: {
    result?: {
        projectElementNodes?: Partial<ProjectElementNode>[] | null
        updatedElementId?: string | null
        contentItems?: ContentItem[] | null
    } | null
    manufacturers: SelectedManufacturerListing[]
    elementId?: string | null
    nodeId?: string | null
}): BulkAddWorkspaceMessages => {
    const { projectElementNodes, updatedElementId, contentItems } =
        input.result ?? {}

    const elementId = updatedElementId || input.elementId || ''
    const newPTNode = projectElementNodes?.find(
        (node) => node?.elementId === elementId
    )
    const nodeId = newPTNode?.id || input.nodeId

    return {
        elementId,
        nodeId,
        manufacturerSelections: input.manufacturers.map((manufacturer) => ({
            listingId: manufacturer.listingId,
            isSelected: true,
            isFirstBOD: false,
            listingText: manufacturer.firmName,
            listingType: manufacturer.type,
            firmName: manufacturer.firmName,
            // Bulk selections are always regular products, never BOD.
            isBOD: false,
            hasSupportedManufacturersOptions: false,
            elementId,
            nodeId,
            isFromDialogMutation: true,
        })) as WorkspaceManufacturerSelected[],
        workspaceItems: Array.isArray(contentItems)
            ? {
                  contentItems,
                  input: {
                      listingId: input.manufacturers[0]?.listingId,
                      isBOD: false,
                      elementId,
                  },
              }
            : null,
    }
}
